import React, { useState, useRef, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import Fuse from "fuse.js";
import { services } from "./servicesData";

// --- Animations ---
const fadeInScale = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.85);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
`;

const typingBlink = keyframes`
  0%, 80%, 100% {
    opacity: 0.3;
    transform: scale(0.8);
  }
  40% {
    opacity: 1;
    transform: scale(1);
  }
`;

// --- Styled Components ---
const ChatbotContainer = styled.div`
  padding: 80px 20px;
  max-width: 600px;
  margin: auto;
  font-family: ${(props) => props.theme.fonts.base};
  color: ${(props) => props.theme.colors.text};
`;

const ChatWindow = styled.div`
  background: ${(props) => props.theme.colors.background};
  border-radius: 16px;
  padding: 20px;
  height: 500px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
`;

const MessageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: ${(props) => (props.isUser ? "flex-end" : "flex-start")};
  margin-bottom: 10px;
`;

const Message = styled.div`
  background: ${(props) =>
    props.isUser ? props.theme.colors.primary : props.theme.colors.extralight};
  color: ${(props) => (props.isUser ? "#fff" : "#000")};
  padding: 10px 14px;
  border-radius: 12px;
  max-width: 80%;
  white-space: pre-line;
  animation: ${fadeInScale} 0.3s ease forwards;
`;

const Timestamp = styled.span`
  font-size: 0.75rem;
  color: #999;
  margin-top: 4px;
`;

const InputWrapper = styled.div`
  display: flex;
  margin-top: 16px;
`;

const Input = styled.input`
  flex: 1;
  padding: 12px;
  font-size: 1rem;
  border: 1px solid #ccc;
  border-radius: 8px;
  margin-right: 8px;
`;

const SendButton = styled.button`
  background: ${(props) => props.theme.colors.primary};
  color: white;
  border: none;
  padding: 12px 20px;
  border-radius: 8px;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const TypingDotsWrapper = styled.div`
  display: flex;
  gap: 6px;
  padding: 10px 14px;
  background: ${(props) => props.theme.colors.extralight};
  border-radius: 12px;
  max-width: 60px;
  animation: ${fadeInScale} 0.3s ease forwards;
`;

const TypingDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #999;
  animation: ${typingBlink} 1.4s infinite ease-in-out;
  animation-delay: ${(props) => props.delay};
`;

// --- Utility ---
const formatTime = (date) =>
  date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

// ✨ Normalize Input Function
const normalizeInput = (text) => {
  return text
    .toLowerCase()
    .replace(
      /(what is|tell me about|explain|describe|can you explain|give info on|details of|how does|how do|do you provide|information on|i want to know about)\s*/gi,
      ""
    )
    .trim();
};

const TypingIndicator = () => (
  <TypingDotsWrapper>
    <TypingDot delay="0s" />
    <TypingDot delay="0.2s" />
    <TypingDot delay="0.4s" />
  </TypingDotsWrapper>
);

const Chatbot = () => {
  const [messages, setMessages] = useState([
    {
      text:
        "I'm here to assist you! Ask me anything about our services – Power Quality Analysis, Smart Grid Monitoring, AMMS, or Transformer Inspection.",
      isUser: false,
      timestamp: new Date()
    }
  ]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const chatRef = useRef(null);

  const fuse = new Fuse(services, {
    keys: ["title", "description", "details", "keywords"],
    threshold: 0.3
  });

  useEffect(() => {
    chatRef.current?.scrollTo({
      top: chatRef.current.scrollHeight,
      behavior: "smooth"
    });
  }, [messages, isTyping]);

  const greetings = [
    "hi",
    "hello",
    "hey",
    "how are you",
    "good morning",
    "good evening"
  ];
  const greetingReply =
    "Hello! How can I assist you today? You can ask about any of our services.";

  const handleSend = async () => {
    if (!input.trim() || isLoading || isTyping) return;

    const userText = input.trim();
    const timestamp = new Date();

    // Add user message
    setMessages((prev) => [...prev, { text: userText, isUser: true, timestamp }]);
    setInput("");
    setIsLoading(true);

    const lowerText = userText.toLowerCase();
    const cleanedText = normalizeInput(lowerText);

    let botReply;

    if (greetings.includes(cleanedText)) {
      botReply = greetingReply;
    } else {
      const result = fuse.search(cleanedText);
      if (result.length > 0) {
        const match = result[0].item;
        botReply = `${match.title}\n\n${match.details}`;
      } else {
        botReply = (
          <span>
            Hmm, I couldn't find an exact match for that.{" "}
            Try asking about one of our key services like{" "}
            <strong>Power Quality</strong>, <strong>AMMS</strong>, or{" "}
            <strong>Transformer Inspection</strong>.<br />
            <br />
            Or{" "}
            <a
              href="https://wa.me/971585971951"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                backgroundColor: "#25D366",
                color: "#fff",
                padding: "8px 12px",
                borderRadius: "6px",
                textDecoration: "none",
                display: "inline-block",
                marginTop: "10px"
              }}
            >
              💬 Chat with our team on WhatsApp
            </a>
          </span>
        );
      }
    }

    // Calculate typing duration based on message length (capped)
    const plainText =
      typeof botReply === "string"
        ? botReply
        : botReply.props.children
        ? botReply.props.children
            .filter((c) => typeof c === "string")
            .join(" ")
        : "";

    const typingDuration = Math.min(3000, plainText.length * 50);

    setIsTyping(true);

    setTimeout(() => {
      setMessages((prev) => [
        ...prev,
        { text: botReply, isUser: false, timestamp: new Date() }
      ]);
      setIsLoading(false);
      setIsTyping(false);
    }, typingDuration);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") handleSend();
  };

  return (
    <ChatbotContainer>
      <ChatWindow ref={chatRef}>
        {messages.map((msg, index) => (
          <MessageWrapper key={index} isUser={msg.isUser}>
            <Message isUser={msg.isUser}>
              {typeof msg.text === "string" ? msg.text : msg.text}
            </Message>
            <Timestamp>{formatTime(new Date(msg.timestamp))}</Timestamp>
          </MessageWrapper>
        ))}

        {isTyping && (
          <MessageWrapper isUser={false}>
            <TypingIndicator />
          </MessageWrapper>
        )}
      </ChatWindow>
      <InputWrapper>
        <Input
          type="text"
          value={input}
          placeholder="Type your question..."
          onChange={(e) => setInput(e.target.value)}
          onKeyPress={handleKeyPress}
          disabled={isLoading || isTyping}
        />
        <SendButton
          onClick={handleSend}
          disabled={!input.trim() || isLoading || isTyping}
        >
          {isLoading || isTyping ? "..." : "Send"}
        </SendButton>
      </InputWrapper>
    </ChatbotContainer>
  );
};

export default Chatbot;
